/**
 * **Tab di dalam formulir** — memendekkan laci panjang tanpa menyembunyikan yang wajib.
 *
 * ### ⛔⛔ Jebakan yang membuat komponen ini ada: tab yang belum dibuka TAK TERDAFTAR
 * AntD `Tabs` hanya me-*mount* panel yang aktif. Panel yang belum pernah dibuka berarti
 * `Form.Item`-nya **tak pernah terdaftar** ke formulir — dan akibatnya dua-duanya senyap:
 * `initialValue`-nya tak terpakai, **dan `rules: [{ required }]`-nya tak pernah dijalankan**.
 * Outlet bisa tersimpan tanpa zona waktu hanya karena tabnya tak diketuk.
 *
 * ⇒ Tiap panel dipasang `forceRender`. Tab di sini murni soal TATA LETAK; ia tak boleh punya
 *   pengaruh sedikit pun pada apa yang divalidasi dan apa yang terkirim.
 *
 * ### ⭐ Galat tak boleh sembunyi di tab yang tak terlihat
 * Tanpa penanda, menekan Simpan dengan isian kurang di tab lain terlihat seperti tombol yang
 * **tidak melakukan apa-apa** — galatnya ada, cuma di halaman yang sedang tak dipandang. Karena
 * itu: titik merah di judul tabnya, lalu **lompat sendiri** ke tab bermasalah yang pertama.
 */
import { Badge, Form, Tabs } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';

export interface FormTabItem {
  readonly key: string;
  readonly label: string;
  /**
   * Nama `Form.Item` yang tinggal di tab ini.
   *
   * ⚠️ Dipakai memetakan galat → tab. Nama yang lupa didaftarkan di sini tak akan menyalakan
   * penandanya — galatnya tetap ada, tapi kembali tak terlihat. Itu sebabnya ia wajib, bukan opsional.
   */
  readonly names: readonly string[];
  readonly children: ReactNode;
}

export interface FormTabsProps {
  readonly items: readonly FormTabItem[];
  /** Prefiks `data-testid`: judul tab jadi `<prefix>-tab-<key>`. */
  readonly testId: string;
}

export function FormTabs({ items, testId }: FormTabsProps) {
  /*
   * ⚠️ `shouldUpdate` tanpa syarat: ia yang membuat blok ini digambar ulang tiap kali keadaan
   * field berubah — termasuk sesudah `validateFields()` menandai yang kurang. Tanpa itu, penanda
   * merahnya baru muncul saat pengguna mengetik sesuatu yang lain.
   */
  return (
    <Form.Item noStyle shouldUpdate>
      {(form) => {
        const salah = new Set(
          form
            .getFieldsError()
            .filter((f) => f.errors.length > 0)
            .map((f) => String(f.name[0])),
        );
        return <Isi items={items} testId={testId} salah={salah} />;
      }}
    </Form.Item>
  );
}

function Isi({
  items, testId, salah,
}: FormTabsProps & { readonly salah: ReadonlySet<string> }) {
  const [aktif, setAktif] = useState(items[0]?.key ?? '');

  /** Kunci stabil supaya efek di bawah tak berputar tiap render. */
  const kunci = [...salah].sort().join(',');

  useEffect(() => {
    if (salah.size === 0) return;
    const sekarang = items.find((i) => i.key === aktif);
    // Sudah di tab yang bermasalah ⇒ jangan lompat; pengguna sedang memperbaikinya.
    if (sekarang?.names.some((n) => salah.has(n)) === true) return;
    const tujuan = items.find((i) => i.names.some((n) => salah.has(n)));
    if (tujuan !== undefined) setAktif(tujuan.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kunci]);

  return (
    <Tabs
      activeKey={aktif}
      onChange={setAktif}
      items={items.map((i) => {
        const bermasalah = i.names.some((n) => salah.has(n));
        return {
          key: i.key,
          /* ⛔ `forceRender`: lihat catatan di kepala berkas — tanpa ini, isian wajib di tab yang
             belum dibuka tak pernah divalidasi. */
          forceRender: true,
          label: (
            <span data-testid={`${testId}-tab-${i.key}`} data-bermasalah={String(bermasalah)}>
              {bermasalah ? <Badge status="error" /> : null}
              {i.label}
            </span>
          ),
          children: i.children,
        };
      })}
    />
  );
}
